import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    TimePicker,
    DatePicker,
} from 'antd';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';

import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import type { Dayjs } from "dayjs";


import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

dayjs.extend(duration);
// dayjs.extend(utc);
// dayjs.extend(timezone);
const { RangePicker } = DatePicker;

const { TextArea } = Input;
const { Option } = Select;



interface LeaveModelProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialLeaveValues?: {
        leave_id?: number;
        leave_ledger_id?: number;
        leave_type?: string;
        leave_attendance_type?: string;
        leave_date_start?: string;
        leave_date_end?: string;
        leave_status: string;
        leave_remark: string;
    } | null;
    isEditLeaveMode?: boolean;
    ledgerId: number | null;
}

const leaveTypeOptions = [
    { label: 'Pending', value: 'Pending' },
    { label: 'Confirm', value: 'Confirm' },
    { label: 'Cancel', value: 'Cancel' } // (Spelling kept same as your enum)
];



const EmployeeLeaveModal: React.FC<LeaveModelProps> = ({
    visible,
    onCancel,
    onCreate,
    initialLeaveValues = null,
    isEditLeaveMode = false,
    ledgerId,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);
    const [leaveType, setLeaveType] = useState("single");


    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };

    const formatAsLocalDate = (d: Dayjs) => d.format("YYYY-MM-DD");


    // Handle form submit
    const handleAddLeave = async (values: any) => {
        try {
            let leave_date_start = null;
            let leave_date_end = null;

            // Single Day
            if (leaveType === "single") {
                leave_date_start = values.leave_date.format("YYYY-MM-DD");
                leave_date_end = values.leave_date.format("YYYY-MM-DD");
            }

            // Multiple Days
            if (leaveType === "multiple") {
                leave_date_start = values.leave_date_range[0].format("YYYY-MM-DD");
                leave_date_end = values.leave_date_range[1].format("YYYY-MM-DD");
            }




            const dataVal = {
                leave_ledger_id: ledgerId,
                leave_type: values.leave_type,
                leave_attendance_type: values.leave_attendance_type,
                leave_date_start: leave_date_start,
                leave_date_end: leave_date_end,
                // leave_status: values.leave_status,
                leave_remark: values.leave_remark,
                ...(initialLeaveValues?.leave_id && { leave_id: initialLeaveValues.leave_id }),
            };
            const url = isEditLeaveMode
                ? 'edit-leave'
                : 'add-leave';

            const method = isEditLeaveMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });
            console.log(dataVal);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditLeaveMode ? 'leave updated successfully!' : ' leave created successfully!',
            );
            form.resetFields();

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditLeaveMode ? 'update' : 'create'} leave`);
        }
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditLeaveMode && initialLeaveValues) {
            form.setFieldsValue({
                leave_type: initialLeaveValues.leave_type,
                leave_attendance_type: initialLeaveValues.leave_attendance_type,
                leave_date_start: initialLeaveValues.leave_date_start,
                leave_date_end: initialLeaveValues.leave_date_end,
                leave_remark: initialLeaveValues.leave_remark,
                // leave_status: initialLeaveValues.leave_status,
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditLeaveMode, initialLeaveValues, form]);




    return (
        <Modal
            title={isEditLeaveMode ? 'Edit leave' : 'Add New leave'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnHidden
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddLeave}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        {/* Leave Type Dropdown */}
                        <Form.Item
                            name="leave_type"
                            label="Leave Type"
                            rules={[{ required: true, message: "Please select leave type" }]}
                        >
                            <Select
                                onChange={setLeaveType}
                                options={[
                                    { label: "Single Day", value: "single" },
                                    { label: "Multiple Days", value: "multiple" },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                    {/* single and multiple date leave */}
                    <Col span={12}>
                        {/* Single Day DatePicker */}
                        {leaveType === "single" && (
                            <Form.Item
                                name="leave_date"
                                label="Select Date"
                                rules={[{ required: true, message: "Please choose a date" }]}
                            >
                                <DatePicker style={{ width: "100%", }} format="DD-MM-YYYY" />
                            </Form.Item>
                        )}

                        {/* Multiple Days RangePicker */}
                        {leaveType === "multiple" && (
                            <Form.Item
                                name="leave_date_range"
                                label="Select Start & End Date"
                                rules={[{ required: true, message: "Please choose date range" }]}
                            >
                                <RangePicker style={{ width: "100%", }} format="DD-MM-YYYY" />
                            </Form.Item>
                        )}
                    </Col>

                    {/* Leave Attendance Type Dropdown */}
                    <Col span={12}>
                        <Form.Item
                            name="leave_attendance_type"
                            label="Leave Atendance Type"
                            rules={[{ required: true, message: "Please select leave attendance type" }]}
                        >
                            <Select
                                // onChange={setLeaveType}
                                options={[
                                    { label: "Unpaid Leave", value: "Unpaid Leave" },
                                    { label: "Paid Leave", value: "Paid Leave" },
                                    { label: "Half Day", value: "Half Day" },
                                    { label: "Holiday", value: "Holiday" },
                                    { label: "auto", value: "auto" },
                                ]}
                            />
                        </Form.Item>
                    </Col>

                    {/* Remark */}
                    <Col span={24}>
                        <Form.Item
                            label="Remark"
                            name="leave_remark"
                            rules={[{ required: true, message: "Please enter a remark" }]}
                        >
                            <TextArea
                                rows={4}
                                placeholder="Enter your remark"
                            />
                        </Form.Item>
                    </Col>


                    {/* Type */}
                    {/* <Col span={12}>
                        <Form.Item
                            name="leave_status"
                            className="px-9"
                            label="Type of Work"
                            rules={[
                                { required: true, message: 'Please enter Type of Leave' },
                                { max: 50, message: 'Maximum 50 characters allowed' },
                            ]}
                        >
                            <Select
                                showSearch
                                placeholder="Select type of work"
                                optionFilterProp="label"
                                style={{ width: '100%' }}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={leaveTypeOptions}
                            />
                        </Form.Item>
                    </Col> */}

                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {/* {!isEditLeaveMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New Leave
                        </Button>} */}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default EmployeeLeaveModal;
